import { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Document } from '@tiptap/extension-document';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { Bold } from '@tiptap/extension-bold';
import { Italic } from '@tiptap/extension-italic';
import { Link } from '@tiptap/extension-link';
import { fragmentToHtml, htmlToFragment } from '../util/richtext';
import { EditorView } from '@tiptap/pm/view';
import { TextSelection } from '@tiptap/pm/state';
import './rich-text-editor.css';

export interface RichTextEditorProps {
  value: string;
  onChange: (fragment: string) => void;
  placeholder?: string;
}

export function RichTextEditor({ value, onChange, placeholder = 'Optional details.' }: RichTextEditorProps) {
  const [, setTick] = useState(0);
  const lastEmitted = useRef(value);

  const editor = useEditor({
    extensions: [Document, Paragraph, Text, Bold, Italic, Link.configure({ openOnClick: false })],
    content: fragmentToHtml(value),
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const fragment = htmlToFragment(html);
      lastEmitted.current = fragment;
      onChange(fragment);
    },
    onSelectionUpdate: () => {
      // Force re-render when cursor/selection changes so toolbar button states update
      setTick(t => t + 1);
    },
    editorProps: {
      handleDoubleClick: (view: EditorView, pos: number) => {
        // Handle double-click to select word in standard browser style
        const $pos = view.state.doc.resolve(pos);

        // Find word boundaries by scanning left and right
        const node = $pos.parent;
        const textContent = node.textContent;
        const offset = pos - $pos.start();

        // Scan left for word boundary
        let leftIdx = offset;
        while (leftIdx > 0 && /\w/.test(textContent[leftIdx - 1])) {
          leftIdx--;
        }

        // Scan right for word boundary
        let rightIdx = offset;
        while (rightIdx < textContent.length && /\w/.test(textContent[rightIdx])) {
          rightIdx++;
        }

        // If we didn't find a word (clicked on whitespace), return false to let default happen
        if (leftIdx === rightIdx) {
          return false;
        }

        const start = $pos.start() + leftIdx;
        const end = $pos.start() + rightIdx;

        // Create a selection for the word
        const tr = view.state.tr;
        tr.setSelection(new TextSelection(view.state.doc.resolve(start), view.state.doc.resolve(end)));
        view.dispatch(tr);

        return true; // We handled the event
      },
    },
  });

  // Sync editor content when value changes (e.g., switching between agenda items).
  // Guard against re-triggering on every keystroke by checking if the value matches
  // what the editor itself last emitted (cheap string compare).
  // After setContent, reset the cursor to position 0 so the toolbar reflects the new
  // item's state at the start rather than an inherited position from the previous item.
  useEffect(() => {
    if (!editor) return;
    // Early return if this is the value the editor itself emitted (via onChange)
    if (value === lastEmitted.current) return;
    // Genuine external change detected — sync it into the editor
    const current = htmlToFragment(editor.getHTML());
    if (current !== value) {
      editor.commands.setContent(fragmentToHtml(value), { emitUpdate: false });
      editor.commands.setTextSelection(0);
      lastEmitted.current = value;
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const toggleBold = () => {
    // Use setMark/unsetMark instead of toggleMark to preserve other marks
    const isBoldActive = editor.isActive('bold');
    if (isBoldActive) {
      editor.chain().focus().unsetMark('bold').run();
    } else {
      editor.chain().focus().setMark('bold').run();
    }
  };

  const toggleItalic = () => {
    // Use setMark/unsetMark instead of toggleMark to preserve other marks
    const isItalicActive = editor.isActive('italic');
    if (isItalicActive) {
      editor.chain().focus().unsetMark('italic').run();
    } else {
      editor.chain().focus().setMark('italic').run();
    }
  };
  const toggleLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      editor.chain().focus().toggleLink({ href: url }).run();
    }
  };

  return (
    <div className="rich-text-editor">
      <div className="rte-toolbar">
        <button
          className={`rte-toolbar-btn${editor.isActive('bold') ? ' active' : ''}`}
          onClick={toggleBold}
          title="Bold (Ctrl+B)"
          type="button"
        >
          <strong>B</strong>
        </button>
        <button
          className={`rte-toolbar-btn${editor.isActive('italic') ? ' active' : ''}`}
          onClick={toggleItalic}
          title="Italic (Ctrl+I)"
          type="button"
        >
          <em>I</em>
        </button>
        <button
          className={`rte-toolbar-btn${editor.isActive('link') ? ' active' : ''}`}
          onClick={toggleLink}
          title="Link"
          type="button"
        >
          🔗
        </button>
      </div>
      <EditorContent
        editor={editor}
        className="rte-content"
        aria-label={placeholder}
      />
    </div>
  );
}
